import { useEffect, useState } from 'react';
import { getSignatureBypassOptionsTasksSignatureBypassOptionsGet } from '../../services/api/sdk.gen';
import type { SignatureBypassOptionsResponse } from '../../services/api/types.gen';

/**
 * Bypass reason options, served by the backend.
 *
 * The labels are owned by `SIGNATURE_BYPASS_REASON_LABELS` so the picker, the
 * reporting dataset and any print view render identical strings — the same
 * single-source-of-truth arrangement `useMissedVisitOptions` uses.
 */
export function useSignatureBypassOptions(enabled: boolean) {
  const [options, setOptions] = useState<SignatureBypassOptionsResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || options) return;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const resp =
          await getSignatureBypassOptionsTasksSignatureBypassOptionsGet();
        // The generated client does not throw by default; a failed call comes
        // back as `resp.error` with `resp.data` undefined.
        if (!cancelled && resp.data) {
          setOptions(resp.data);
        } else if (!cancelled && resp.error) {
          console.error('Failed to load bypass reason options', resp.error);
        }
      } catch (err) {
        // `finally` alone left anything thrown below the client's
        // resolve-don't-throw behaviour to escape as an unhandled rejection,
        // because this call is fire-and-forget. Leaving `options` null keeps
        // the reason picker empty and the submit button disabled — a bypass
        // cannot be recorded without a reason the backend served.
        if (!cancelled) {
          console.error('Failed to load bypass reason options', err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, options]);

  return { options, isLoading };
}
